import { Controller } from "@hotwired/stimulus"

// Kicks off the asynchronous Zeitleiste PDF export.
//
// The whole view state is already baked into urlValue as a query string (built
// server-side by ToolbarComponent#export_url_params), so this controller only has to
// fire the request and report back. The PDF is produced by a background job and shows
// up under /exports, which is why the timeline itself never blocks.
export default class extends Controller {
  static values = {
    url: String,
    exportsUrl: String,
    startedMessage: String,
    errorMessage: String,
    exportsLabel: String,
  }

  static targets = ["label", "spinner"]

  async start(event) {
    event.preventDefault()
    if (this.busy) return

    this.setBusy(true)

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
          Accept: "application/json",
        },
      })

      const json = await response.json()

      if (json.success) {
        this.notify(json.message || this.startedMessageValue, "success", true)
      } else {
        this.notify(json.error || this.errorMessageValue, "error", false)
      }
    } catch (error) {
      console.error("Zeitleiste PDF export failed:", error)
      this.notify(this.errorMessageValue, "error", false)
    } finally {
      this.setBusy(false)
    }
  }

  setBusy(busy) {
    this.busy = busy
    if (this.hasLabelTarget) this.labelTarget.classList.toggle("hidden", busy)
    if (this.hasSpinnerTarget) this.spinnerTarget.classList.toggle("hidden", !busy)
  }

  // Mirrors shared/_notification, the partial the e-mail-share path turbo-streams into
  // the same #notifications container, so both Export actions produce identical toasts.
  notify(message, type, withLink) {
    // The container is server-rendered by the full-page views, but the timeline frame can
    // be hosted by a page without it (e.g. after a tab frame navigation). Create it on
    // document.body then — body-level also keeps it out of <main>'s z-0 stacking context.
    let container = document.getElementById("notifications")
    if (!container) {
      container = document.createElement("div")
      container.id = "notifications"
      container.className = "fixed top-4 right-4 z-50 w-96"
      document.body.appendChild(container)
    }

    const success = type === "success"
    const toast = document.createElement("div")
    toast.className = `rounded-md p-4 mb-4 border ${
      success ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"
    }`
    toast.setAttribute("data-controller", "notification")
    toast.setAttribute("data-notification-dismiss-after-value", "8000")

    const body = document.createElement("div")
    body.className = "flex items-center"

    const text = document.createElement("p")
    text.className = `text-sm font-medium ${success ? "text-green-800" : "text-red-800"}`
    text.textContent = message
    body.appendChild(text)

    if (withLink && this.hasExportsUrlValue) {
      const link = document.createElement("a")
      link.href = this.exportsUrlValue
      link.className = `ml-3 shrink-0 text-sm font-semibold underline ${success ? "text-green-800" : "text-red-800"}`
      link.textContent = this.exportsLabelValue
      body.appendChild(link)
    }

    toast.appendChild(body)
    container.prepend(toast)
  }
}
